import { recruitingAPI } from "../../services/recruitingApi";
import {
  createPersonnelAssignmentHook,
  type UsePersonnelAssignmentOptions,
  type AssignedPerson,
  type AvailablePerson,
  type NotificationState,
  type ConfirmDialogState,
} from "./usePersonnelAssignment";

export interface TechChecker extends AssignedPerson {
  tech_checker_id: number;
  tech_checker_name?: string;
}

export type AvailableTechChecker = AvailablePerson;

const usePersonnelAssignment = createPersonnelAssignmentHook<TechChecker>({
  roleSlug: "tech_checker",
  assignedQueryKey: "candidate-tech-checkers",
  availableQueryKey: "tech-checkers-list",
  assignedDataKey: "assigned_tech_checkers",
  fetchAssigned: (candidateId) =>
    recruitingAPI.getTechCheckersForCandidate(candidateId),
  assign: (candidateId, techCheckerId) =>
    recruitingAPI.assignTechCheckerToCandidate(candidateId, techCheckerId),
  remove: (candidateId, techCheckerId) =>
    recruitingAPI.removeTechCheckerFromCandidate(candidateId, techCheckerId),
  label: "Tech checker",
  gateAssignedOnPermission: true,
  getPersonnelId: (tc) => tc.tech_checker_id,
});

export interface UseTechCheckerAssignmentReturn {
  techCheckers: TechChecker[];
  availableTechCheckers: AvailableTechChecker[];
  selectedTechCheckerId: string;
  setSelectedTechCheckerId: (id: string) => void;
  isAssigningTechChecker: boolean;
  handleAssignTechChecker: (overrideId?: string) => Promise<void>;
  handleRemoveTechChecker: (techCheckerId: number) => void;
  canManageTechCheckers: boolean;
  notification: NotificationState;
  closeNotification: () => void;
  confirmDialog: ConfirmDialogState;
  confirmRemoval: () => void;
  cancelRemoval: () => void;
}

/**
 * Custom hook for managing tech checker assignments for a candidate.
 * Wraps the shared usePersonnelAssignment hook with tech-checker-specific naming.
 */
export function useTechCheckerAssignment(
  options: UsePersonnelAssignmentOptions,
): UseTechCheckerAssignmentReturn {
  const result = usePersonnelAssignment(options);

  return {
    techCheckers: result.assigned,
    availableTechCheckers: result.available,
    selectedTechCheckerId: result.selectedId,
    setSelectedTechCheckerId: result.setSelectedId,
    isAssigningTechChecker: result.isAssigning,
    handleAssignTechChecker: result.handleAssign,
    handleRemoveTechChecker: result.handleRemove,
    canManageTechCheckers: result.canManage,
    notification: result.notification,
    closeNotification: result.closeNotification,
    confirmDialog: result.confirmDialog,
    confirmRemoval: result.confirmRemoval,
    cancelRemoval: result.cancelRemoval,
  };
}

export default useTechCheckerAssignment;
